import { Controller } from "@hotwired/stimulus"

const RECENT_KEY = "afagh-cmdk-recent"
const RECENT_MAX = 8
const SEARCH_DEBOUNCE_MS = 200

// Ctrl+K / Cmd+K command palette: content search (fetched from the
// server), dashboard navigation, recently visited pages, page-specific
// actions, documentation links, and preferences (theme/locale/sign out)
// — all merged into one filterable, keyboard-navigable list.
export default class extends Controller {
  static targets = ["config", "backdrop", "dialog", "input", "list", "themeButton", "logoutButton"]

  connect() {
    const config = JSON.parse(this.configTarget.textContent)
    this.searchUrl = config.search_url
    this.staticItems = config.static_items
    this.labels = config.labels
    this.remoteResults = []
    this.results = []
    this.activeIndex = 0
    this.isOpen = false

    this.onGlobalKeydown = this.onGlobalKeydown.bind(this)
    document.addEventListener("keydown", this.onGlobalKeydown)

    this.recordVisit()
  }

  disconnect() {
    document.removeEventListener("keydown", this.onGlobalKeydown)
    clearTimeout(this.searchTimeout)
  }

  onGlobalKeydown(event) {
    // event.code (the physical key, "KeyK") is checked alongside event.key
    // because focus inside a text input makes some browsers (Chrome/Firefox
    // on Linux in particular) report Ctrl/Cmd+K's `key` as a raw control
    // character instead of "k" — which silently broke the shortcut the
    // moment the palette's own search input had focus (i.e. every time
    // after the first open).
    if ((event.metaKey || event.ctrlKey) && (event.key.toLowerCase() === "k" || event.code === "KeyK")) {
      event.preventDefault()
      this.isOpen ? this.close() : this.open()
    } else if (event.key === "Escape" && this.isOpen) {
      this.close()
    }
  }

  open() {
    this.isOpen = true
    this.backdropTarget.hidden = false
    this.dialogTarget.hidden = false
    this.inputTarget.value = ""
    this.remoteResults = []
    this.renderResults("")
    requestAnimationFrame(() => this.inputTarget.focus())
  }

  close() {
    this.isOpen = false
    this.inputTarget.blur()
    this.backdropTarget.hidden = true
    this.dialogTarget.hidden = true
  }

  onInput() {
    const query = this.inputTarget.value.trim()
    this.renderResults(query)

    clearTimeout(this.searchTimeout)
    if (query.length < 2) {
      this.remoteResults = []
      return
    }
    this.searchTimeout = setTimeout(() => this.fetchResults(query), SEARCH_DEBOUNCE_MS)
  }

  onKeydown(event) {
    if (event.key === "ArrowDown") {
      event.preventDefault()
      this.move(1)
    } else if (event.key === "ArrowUp") {
      event.preventDefault()
      this.move(-1)
    } else if (event.key === "Enter") {
      event.preventDefault()
      this.select(this.activeIndex)
    }
  }

  async fetchResults(query) {
    try {
      const response = await fetch(`${this.searchUrl}?q=${encodeURIComponent(query)}`, {
        headers: { Accept: "application/json" },
      })
      if (!response.ok) return

      const data = await response.json()
      if (this.inputTarget.value.trim() !== query) return // stale response, query moved on

      this.remoteResults = data.results || []
      this.renderResults(query)
    } catch (e) {
      // Network hiccup — leave whatever local results are already showing.
    }
  }

  move(delta) {
    if (this.results.length === 0) return

    this.activeIndex = (this.activeIndex + delta + this.results.length) % this.results.length
    this.highlight()
  }

  highlight() {
    this.listTarget.querySelectorAll(".cmdk-item").forEach((el, index) => {
      el.classList.toggle("active", index === this.activeIndex)
    })
    this.listTarget.querySelector(".cmdk-item.active")?.scrollIntoView({ block: "nearest" })
  }

  select(index) {
    const item = this.results[index]
    if (!item) return

    this.close()

    if (item.action === "theme") {
      this.themeButtonTarget.click()
    } else if (item.action === "logout") {
      this.logoutButtonTarget.click()
    } else if (item.url) {
      Turbo.visit(item.url)
    }
  }

  renderResults(query) {
    this.results = this.buildResults(query)
    this.activeIndex = 0
    this.listTarget.replaceChildren()

    if (this.results.length === 0) {
      const empty = document.createElement("div")
      empty.className = "cmdk-empty"
      empty.textContent = this.labels.no_results
      this.listTarget.appendChild(empty)
      return
    }

    let lastGroup = null
    this.results.forEach((item, index) => {
      if (item.group !== lastGroup) {
        lastGroup = item.group
        const label = document.createElement("div")
        label.className = "cmdk-group-label"
        label.textContent = this.labels.groups[item.group] || item.group
        this.listTarget.appendChild(label)
      }
      this.listTarget.appendChild(this.buildRow(item, index))
    })
  }

  buildRow(item, index) {
    const row = document.createElement("div")
    row.className = "cmdk-item" + (index === this.activeIndex ? " active" : "")
    row.addEventListener("click", () => this.select(index))
    row.addEventListener("mousemove", () => {
      if (this.activeIndex !== index) {
        this.activeIndex = index
        this.highlight()
      }
    })

    const title = document.createElement("span")
    title.className = "cmdk-title"
    title.textContent = item.title
    row.appendChild(title)

    if (item.subtitle) {
      const subtitle = document.createElement("span")
      subtitle.className = "cmdk-subtitle"
      subtitle.textContent = item.subtitle
      row.appendChild(subtitle)
    }

    return row
  }

  buildResults(query) {
    const q = query.toLowerCase()

    if (q.length === 0) {
      const pageActions = this.staticItems.filter((item) => item.group === "page_actions")
      const rest = this.staticItems.filter((item) => item.group !== "page_actions")
      return [...pageActions, ...this.recentItems(), ...rest]
    }

    const matches = (item) =>
      item.title.toLowerCase().includes(q) || (item.subtitle || "").toLowerCase().includes(q)

    return [...this.remoteResults, ...this.staticItems.filter(matches)]
  }

  recentItems() {
    return this.readRecent()
      .slice(0, 5)
      .map((entry) => ({ group: "recent", title: entry.title, url: entry.url }))
  }

  readRecent() {
    try {
      return JSON.parse(localStorage.getItem(RECENT_KEY)) || []
    } catch (e) {
      return []
    }
  }

  // Called on every connect (i.e. every page load / Turbo visit) so the
  // page we just landed on gets recorded as "recently visited" for next time.
  recordVisit() {
    const title = document.title
    if (!title) return

    const url = window.location.pathname + window.location.search
    const stored = this.readRecent().filter((entry) => entry.url !== url)
    stored.unshift({ title, url })

    localStorage.setItem(RECENT_KEY, JSON.stringify(stored.slice(0, RECENT_MAX)))
  }
}
